// Profile management utilities for Supabase
import { supabase } from '../lib/supabase.js';
import {
  safeFetchSingle,
  safeUpdate,
  safeInsert,
  validateRequiredFields,
  checkAuthentication,
  handleSupabaseError
} from './supabaseErrorHandler.js';

const PROFILE_STORAGE_BUCKET = 'profile-images';

// Validation constants
const VALIDATION_RULES = {
  handle: {
    minLength: 3,
    maxLength: 30,
    pattern: null, // Allow any characters including spaces and special characters
    errorMessage: 'Handle must be 3-30 characters'
  },
  bio: {
    maxLength: 500,
    errorMessage: 'Bio must be 500 characters or less'
  },
  profilePictureUrl: {
    maxLength: 2048,
    errorMessage: 'Profile picture URL is too long'
  },
  bannerUrl: {
    maxLength: 2048,
    errorMessage: 'Banner URL is too long'
  }
};

/**
 * Validate and normalize profile input data
 * @param {Object} profileData - Raw profile data
 * @returns {Object} { valid: boolean, data: Object, errors: Array }
 */
const validateAndNormalizeProfileData = (profileData) => {
  const errors = [];
  const normalized = {};
  
  // Validate and normalize handle
  if (profileData.handle !== undefined) {
    const handle = String(profileData.handle).trim();
    
    if (handle.length < VALIDATION_RULES.handle.minLength) {
      errors.push(`Handle must be at least ${VALIDATION_RULES.handle.minLength} characters`);
    } else if (handle.length > VALIDATION_RULES.handle.maxLength) {
      errors.push(`Handle must be ${VALIDATION_RULES.handle.maxLength} characters or less`);
    } else if (VALIDATION_RULES.handle.pattern && !VALIDATION_RULES.handle.pattern.test(handle)) {
      errors.push(VALIDATION_RULES.handle.errorMessage);
    } else {
      normalized.handle = handle;
    }
  }
  
  // Validate and normalize bio
  if (profileData.bio !== undefined) {
    const bio = profileData.bio ? String(profileData.bio).trim() : null;
    if (bio && bio.length > VALIDATION_RULES.bio.maxLength) {
      errors.push(VALIDATION_RULES.bio.errorMessage);
    } else {
      normalized.bio = bio;
    }
  }
  
  // Validate profile picture URL
  if (profileData.profile_picture_url !== undefined) {
    const url = profileData.profile_picture_url ? String(profileData.profile_picture_url).trim() : null;
    if (url && url.length > VALIDATION_RULES.profilePictureUrl.maxLength) {
      errors.push(VALIDATION_RULES.profilePictureUrl.errorMessage);
    } else {
      normalized.profile_picture_url = url;
    }
  }
  
  // Validate banner URL
  if (profileData.banner_url !== undefined) {
    const url = profileData.banner_url ? String(profileData.banner_url).trim() : null;
    if (url && url.length > VALIDATION_RULES.bannerUrl.maxLength) {
      errors.push(VALIDATION_RULES.bannerUrl.errorMessage);
    } else {
      normalized.banner_url = url;
    }
  }
  
  // Validate social links (must be valid JSON object)
  if (profileData.social_links !== undefined) {
    try {
      const links = profileData.social_links || {};
      if (typeof links === 'object' && !Array.isArray(links)) {
        // Trim all link values
        const trimmedLinks = {};
        for (const [key, value] of Object.entries(links)) {
          if (value) {
            trimmedLinks[key] = String(value).trim();
          }
        }
        normalized.social_links = trimmedLinks;
      } else {
        errors.push('Social links must be a valid object');
      }
    } catch (error) {
      errors.push('Invalid social links format');
    }
  }
  
  return {
    valid: errors.length === 0,
    data: normalized,
    errors
  };
};

/**
 * Check if a handle is already taken by another user
 * @param {string} handle - Handle to check
 * @param {string} currentWallet - Current user's wallet (to exclude self)
 * @returns {Promise<boolean>} - True if handle is taken, false if available
 */
const isHandleTaken = async (handle, currentWallet) => {
  if (!handle) return false;
  
  const normalizedHandle = handle.toLowerCase();
  
  try {
    // Check if handle exists for a different wallet
    const { data, error } = await supabase
      .from('creators')
      .select('wallet')
      .ilike('handle', normalizedHandle) // Case-insensitive match
      .neq('wallet', currentWallet)
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error checking handle availability:', error);
      return false; // On error, assume available to allow operation to proceed
    }
    
    return !!data; // True if handle exists for another user
  } catch (error) {
    console.error('❌ Exception checking handle:', error);
    return false;
  }
};

/**
 * Log profile operation with structured data
 * @param {string} event - Event name (create/update/error)
 * @param {string} wallet - User's wallet address
 * @param {Object} metadata - Additional metadata
 */
const logProfileOperation = (event, wallet, metadata = {}) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    event: `profile_${event}`,
    wallet: wallet ? wallet.slice(0, 8) + '...' : 'unknown', // Truncate for privacy
    ...metadata
  };
  
  console.log(`📊 [Profile Analytics]`, JSON.stringify(logEntry));
};

/**
 * Upload a profile image (profile picture or banner) to Supabase storage
 * @param {File} file - The image file to upload
 * @param {string} walletAddress - User's wallet address
 * @param {string} imageType - 'profile-picture' or 'banner'
 * @returns {Promise<string>} - Public URL of the uploaded image
 */
export const uploadProfileImage = async (file, walletAddress, imageType) => {
  try {
    console.log(`📸 Uploading ${imageType} for wallet:`, walletAddress);
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF images.');
    }
    
    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload images smaller than 5MB.');
    }
    
    // Create unique filename
    const fileExtension = file.name.split('.').pop();
    const fileName = `${imageType}-${Date.now()}.${fileExtension}`;
    const filePath = `${walletAddress}/${fileName}`;
    
    // First, delete any existing image of the same type for this wallet
    try {
      const { data: existingFiles } = await supabase.storage
        .from(PROFILE_STORAGE_BUCKET)
        .list(walletAddress);
      
      if (existingFiles) {
        const existingImage = existingFiles.find(file => 
          file.name.startsWith(imageType)
        );
        
        if (existingImage) {
          console.log(`🗑️ Deleting existing ${imageType}:`, existingImage.name);
          await supabase.storage
            .from(PROFILE_STORAGE_BUCKET)
            .remove([`${walletAddress}/${existingImage.name}`]);
        }
      }
    } catch (deleteError) {
      console.log('ℹ️ No existing image to delete or delete failed:', deleteError);
      // Continue with upload even if delete fails
    }
    
    // Upload to Supabase storage
    const { data, error } = await supabase.storage
      .from(PROFILE_STORAGE_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Allow overwriting existing files
      });
    
    if (error) {
      console.error('❌ Profile image upload error:', error);
      throw error;
    }
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(PROFILE_STORAGE_BUCKET)
      .getPublicUrl(filePath);
    
    console.log('✅ Profile image uploaded successfully:', publicUrl);
    return publicUrl;
    
  } catch (error) {
    console.error('❌ Failed to upload profile image:', error);
    throw error;
  }
};

/**
 * Delete a profile image from Supabase storage
 * @param {string} imageUrl - The URL of the image to delete
 * @returns {Promise<boolean>} - Success status
 */
export const deleteProfileImage = async (imageUrl) => {
  try {
    if (!imageUrl) return true; // Nothing to delete
    
    // Extract file path from URL
    const urlParts = imageUrl.split('/');
    const bucketIndex = urlParts.indexOf(PROFILE_STORAGE_BUCKET);
    if (bucketIndex === -1) {
      console.warn('⚠️ Invalid profile image URL:', imageUrl);
      return false;
    }
    
    const filePath = urlParts.slice(bucketIndex + 1).join('/');
    
    const { error } = await supabase.storage
      .from(PROFILE_STORAGE_BUCKET)
      .remove([filePath]);
    
    if (error) {
      console.error('❌ Failed to delete profile image:', error);
      return false;
    }
    
    console.log('✅ Profile image deleted successfully:', filePath);
    return true;
    
  } catch (error) {
    console.error('❌ Error deleting profile image:', error);
    return false;
  }
};

/**
 * Get or create a creator profile by wallet address
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<StandardResponse>} - Creator profile object
 */
export const getOrCreateCreatorProfile = async (walletAddress) => {
  console.log('👤 Getting or creating creator profile for wallet:', walletAddress);
  
  // Validate input
  const validationError = validateRequiredFields({ walletAddress }, 'getting or creating creator profile');
  if (validationError) {
    console.error('❌ Validation failed:', validationError);
    return validationError;
  }
  
  // First, try to get existing creator
  console.log('🔍 Attempting to fetch existing creator profile...');
  const fetchResult = await safeFetchSingle(
    supabase.from('creators').select('*').eq('wallet', walletAddress),
    'creator profile',
    { wallet: walletAddress }
  );
  
  console.log('📊 Fetch result:', {
    success: fetchResult.success,
    code: fetchResult.code,
    hasData: !!fetchResult.data,
    error: fetchResult.error
  });
  
  // If found, return it
  if (fetchResult.success) {
    console.log('✅ Found existing creator profile:', fetchResult.data);
    return fetchResult;
  }
  
  // If not found (NOT_FOUND), create new creator
  if (fetchResult.code === 'NOT_FOUND') {
    const handle = walletAddress.length >= 8 
      ? `user_${walletAddress.slice(0, 8)}` 
      : `user_${walletAddress}`;
    
    console.log('👤 Creating new creator profile with handle:', handle);
    
    const insertResult = await safeInsert(
      () => supabase
        .from('creators')
        .insert({
          wallet: walletAddress,
          handle: handle,
          is_verified: false,
          bio: null,
          profile_picture_url: null,
          banner_url: null,
          social_links: {}
        })
        .select()
        .single(),
      'creator profile',
      { wallet: walletAddress, handle }
    );
    
    console.log('📊 Insert result:', {
      success: insertResult.success,
      code: insertResult.code,
      hasData: !!insertResult.data,
      error: insertResult.error
    });
    
    return insertResult;
  }
  
  // If other error, return it
  console.error('❌ Unexpected error in getOrCreateCreatorProfile:', fetchResult);
  return fetchResult;
};

/**
 * Update creator profile data (idempotent - creates if doesn't exist)
 * @param {string} walletAddress - User's wallet address
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<StandardResponse>} - Updated creator profile
 */
export const updateCreatorProfile = async (walletAddress, profileData) => {
  const startTime = Date.now();
  console.log('📝 Updating creator profile for wallet:', walletAddress);
  
  // Validate wallet address
  const validationError = validateRequiredFields({ walletAddress }, 'updating creator profile');
  if (validationError) {
    logProfileOperation('validation_error', walletAddress, { error: 'missing_wallet' });
    return validationError;
  }
  
  // Validate and normalize profile data
  const validation = validateAndNormalizeProfileData(profileData);
  if (!validation.valid) {
    const errorMessage = validation.errors.join('; ');
    console.error('❌ Profile validation failed:', validation.errors);
    logProfileOperation('validation_error', walletAddress, { 
      errors: validation.errors,
      duration_ms: Date.now() - startTime
    });
    return {
      success: false,
      data: null,
      error: errorMessage,
      code: 'VALIDATION_ERROR'
    };
  }
  
  // Check handle uniqueness if handle is being updated
  if (validation.data.handle) {
    const handleTaken = await isHandleTaken(validation.data.handle, walletAddress);
    if (handleTaken) {
      console.error('❌ Handle already taken:', validation.data.handle);
      logProfileOperation('handle_conflict', walletAddress, { 
        handle: validation.data.handle,
        duration_ms: Date.now() - startTime
      });
      return {
        success: false,
        data: null,
        error: 'Handle already taken. Please choose a different handle.',
        code: 'HANDLE_CONFLICT'
      };
    }
  }
  
  // First, ensure the creator profile exists (idempotent)
  const profileResult = await getOrCreateCreatorProfile(walletAddress);
  
  if (!profileResult.success) {
    logProfileOperation('get_or_create_error', walletAddress, { 
      error: profileResult.error,
      duration_ms: Date.now() - startTime
    });
    return profileResult; // Return the error
  }
  
  // Prepare update data with normalized values
  const updateData = {
    ...validation.data,
    updated_at: new Date().toISOString()
  };
  
  console.log('📝 Updating with validated data:', updateData);
  
  // Perform the update
  const result = await safeUpdate(
    () => supabase
      .from('creators')
      .update(updateData)
      .eq('wallet', walletAddress)
      .select()
      .maybeSingle(),
    'creator profile',
    { wallet: walletAddress, fields: Object.keys(updateData) }
  );
  
  // Log the operation result
  if (result.success) {
    logProfileOperation('update', walletAddress, { 
      handle: validation.data.handle || 'unchanged',
      fields_updated: Object.keys(validation.data),
      duration_ms: Date.now() - startTime
    });
  } else {
    logProfileOperation('update_error', walletAddress, { 
      error: result.error,
      duration_ms: Date.now() - startTime
    });
  }
  
  return result;
};

/**
 * Get creator profile by wallet address
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<StandardResponse>} - Creator profile or not found error
 */
export const getCreatorProfile = async (walletAddress) => {
  // Validate input
  const validationError = validateRequiredFields({ walletAddress }, 'getting creator profile');
  if (validationError) return validationError;
  
  return await safeFetchSingle(
    supabase.from('creators').select('*').eq('wallet', walletAddress),
    'creator profile',
    { wallet: walletAddress }
  );
};

/**
 * Get creator profile by handle
 * @param {string} handle - Creator's handle
 * @returns {Promise<StandardResponse>} - Creator profile or not found error
 */
export const getCreatorProfileByHandle = async (handle) => {
  // Validate input
  const validationError = validateRequiredFields({ handle }, 'getting creator profile by handle');
  if (validationError) return validationError;
  
  return await safeFetchSingle(
    supabase.from('creators').select('*').eq('handle', handle),
    'creator profile',
    { handle }
  );
};

/**
 * Get creator profile by ID
 * @param {number} creatorId - Creator's ID
 * @returns {Promise<StandardResponse>} - Creator profile or not found error
 */
export const getCreatorProfileById = async (creatorId) => {
  // Validate input
  const validationError = validateRequiredFields({ creatorId }, 'getting creator profile by ID');
  if (validationError) return validationError;
  
  return await safeFetchSingle(
    supabase.from('creators').select('*').eq('id', creatorId),
    'creator profile',
    { id: creatorId }
  );
};